import React, {useState, useMemo, useEffect} from 'react'
import styled from 'styled-components'
import {OTP_COOLDOWN} from 'consts'
import {useTranslation} from 'i18n'
import convertUnit from 'lib/unit'
import {useSelector} from 'lib/redux'
import {Modal, Paragraph, InputOTP, Button, TextTimer} from 'common/components'
import {GiftShopTemplateOTPModalProps} from './GiftShopTemplateOTPModalProps'

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledHeaderContainer = styled(StyledColumn)`
  gap: ${convertUnit(8)};
`

const StyledModal = styled(Modal)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  width: 100%;
  max-width: ${convertUnit(295)};
  padding: ${convertUnit(20)};
  gap: ${convertUnit(20)};
`

const StyledButtonContainer = styled.div`
  display: flex;
  gap: ${convertUnit(20)};
`

const StyledContentContainer = styled(StyledColumn)`
  align-items: center;
  gap: ${convertUnit(4)};
`

const StyledButton = styled(Button)`
  width: 100%;
`

const StyledDescription = styled(Paragraph)`
  white-space: pre-line;
`

export default function GiftShopTemplateOTPModal({
  stateError,
  dataToVerify,
  timeRemaining,
  verificationMethod,
  onVerify,
  onResend,
  onCancel,
  bottomText,
  textAvailable,
  textCooldown,
  footerText,
  resetWhenPress,
}: GiftShopTemplateOTPModalProps) {
  const {translate} = useTranslation()
  const [error, setError] = stateError
  const [codes, setCodes] = useState('')
  const {phone_code, phone_number} = useSelector('user') || {}

  const handleRenderHeader = useMemo(
    () => (
      <StyledHeaderContainer>
        <Paragraph fontWeight="bold" fontSize="l">
          {translate('giftShop:OTPVerification')}
        </Paragraph>
        <StyledDescription fontSize="m" lineHeight={28}>
          {translate('giftShop:OTPVerificationDescription', {
            number: dataToVerify,
            context:
              phone_code && phone_number ? verificationMethod : undefined,
          })}
        </StyledDescription>
        {bottomText ? (
          <Paragraph fontSize="m" fontWeight="bold">
            {bottomText}
          </Paragraph>
        ) : null}
      </StyledHeaderContainer>
    ),
    [
      bottomText,
      dataToVerify,
      phone_code,
      phone_number,
      translate,
      verificationMethod,
    ],
  )

  const handleRenderErrorMessages = useMemo(
    () =>
      error ? (
        <>
          <Paragraph fontSize="m" fontWeight="medium" color="danger_5">
            {translate('giftShop:incorrectOTP')}
          </Paragraph>
          <Paragraph fontSize="m">
            {translate('giftShop:incorrectOTPPleaseEnterCorrect')}
          </Paragraph>
        </>
      ) : (
        <Paragraph fontSize="m">
          {footerText || translate('giftShop:didNotReceiveTheCode')}
        </Paragraph>
      ),
    [error, footerText, translate],
  )

  const handleRenderContent = useMemo(
    () => (
      <>
        <InputOTP onSubmit={(value) => setCodes(value)} />
        <StyledContentContainer>
          {handleRenderErrorMessages}
          <TextTimer
            textAvailable={
              textAvailable || translate('auth:verificationPhoneResend')
            }
            fontSize="m"
            textCooldown="otp"
            onPress={onResend}
            lastSaved={timeRemaining}
            cooldown={OTP_COOLDOWN}
            customTextCooldown={textCooldown}
            resetWhenPress={resetWhenPress}
          />
        </StyledContentContainer>
      </>
    ),
    [
      handleRenderErrorMessages,
      onResend,
      resetWhenPress,
      textAvailable,
      textCooldown,
      timeRemaining,
      translate,
    ],
  )

  const handleRenderButton = useMemo(
    () => (
      <StyledButtonContainer>
        <StyledButton
          label={translate('global:cancel')}
          backgroundColor="white_2"
          backgroundHoverColor="white_3"
          color="primary_5"
          onClick={onCancel}
        />
        <StyledButton
          label={translate('global:confirm')}
          disabled={codes.length < 4}
          onClick={() => onVerify(codes)}
        />
      </StyledButtonContainer>
    ),
    [codes, onCancel, onVerify, translate],
  )

  useEffect(() => {
    if (codes === '') setError(false)
  }, [codes, setError])

  return (
    <StyledModal visible onBackdropPress={onCancel}>
      {handleRenderHeader}
      {handleRenderContent}
      {handleRenderButton}
    </StyledModal>
  )
}
